import React, { FunctionComponent, memo, useMemo } from 'react'
import { animated, Interpolation, SpringValue } from '@react-spring/web'
import styled from 'styled-components'
import { AnimatedGridBasicStylingType } from './AnimatedGridTypes'

type TranslateValue = number | SpringValue | Interpolation
type AnimatedGridProps = AnimatedGridBasicStylingType & {
    testID?: string
    translate?: { translateX?: TranslateValue; translateY?: TranslateValue }
}

type Style = Omit<
    AnimatedGridBasicStylingType,
    'center' | 'noExpand' | 'children' | 'pointerEvents'
> & {
    userSelect: 'none'
    willChange: 'transform'
    translate?: [TranslateValue, TranslateValue]
    flexGrow: number
    flexShrink: number
    flexBasis: string | number
    alignItems?: string
}

const StyledAnimatedDiv = styled(animated.div)<Pick<AnimatedGridBasicStylingType, 'pointerEvents'>>`
    ${(props): string => {
        if (props.pointerEvents) {
            return props.pointerEvents === 'box-none'
                ? 'pointer-events: none;'
                : `pointer-events: ${props.pointerEvents};`
        }
        return ''
    }}
    * {
        ${(props): string => {
            if (props.pointerEvents) {
                return props.pointerEvents === 'box-none' ? 'pointer-events: auto;' : ''
            }
            return ''
        }}
    }
`

const AnimatedGrid: FunctionComponent<AnimatedGridProps> = ({
    noExpand,
    center,
    width,
    height,
    perspective,
    scale,
    translate,
    children,
    opacity,
    justifyContent,
    pointerEvents,
    testID,
    ...rest
}) => {
    const centerStyle = center ? 'center' : undefined
    const style = useMemo<Style>(() => {
        return {
            userSelect: 'none',
            willChange: 'transform',
            opacity,
            width,
            height,
            translateX: translate?.translateX,
            translateY: translate?.translateY,
            flexGrow: noExpand ? 0 : 1,
            flexShrink: 1,
            flexBasis: noExpand ? 'auto' : 0,
            justifyContent: justifyContent ?? centerStyle,
            scale: scale ?? 1,
            perspective,
            alignItems: center ? 'center' : undefined,
            ...rest,
        }
    }, [
        opacity,
        width,
        height,
        translate,
        noExpand,
        justifyContent,
        centerStyle,
        scale,
        perspective,
        center,
        rest,
    ])
    return (
        <StyledAnimatedDiv data-testid={testID} pointerEvents={pointerEvents} style={style}>
            {children}
        </StyledAnimatedDiv>
    )
}

export default memo(AnimatedGrid)
